import { useEffect, useRef, useState } from 'react';

interface Valor {
  icon: string;
  titulo: string;
  descricao: string;
}

const valores: Valor[] = [
  {
    icon: 'ri-heart-line',
    titulo: 'Amor Próprio',
    descricao: 'Acredito que a transformação começa quando você se permite amar quem você é',
  },
  {
    icon: 'ri-lightbulb-line',
    titulo: 'Consciência',
    descricao: 'Despertar para o seu poder interior é o primeiro passo para a liberdade',
  },
  {
    icon: 'ri-hand-heart-line',
    titulo: 'Empatia',
    descricao: 'Cada história importa. Cada dor merece ser vista e transformada',
  },
  {
    icon: 'ri-rocket-line',
    titulo: 'Ação',
    descricao: 'Conhecimento sem prática não transforma. Aqui você age e evolui',
  },
];

const MissaoVisao = () => {
  const [stage, setStage] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const timers = [
            setTimeout(() => setStage(1), 100),
            setTimeout(() => setStage(2), 400),
            setTimeout(() => setStage(3), 700),
            setTimeout(() => setStage(4), 1000),
            setTimeout(() => setStage(5), 1300),
          ];
          return () => timers.forEach(clearTimeout);
        }
      },
      { threshold: 0.2 },
    );

    const current = sectionRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
      observer.disconnect();
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative py-32 bg-gradient-to-b from-luxury-black via-luxury-charcoal/30 to-luxury-black overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-32 right-1/4 w-2 h-2 bg-gold-500/30 rounded-full animate-float" />
      <div
        className="absolute top-48 left-1/3 w-1.5 h-1.5 bg-gold-400/20 rounded-full animate-float"
        style={{ animationDelay: '1.5s' }}
      />
      <div
        className="absolute bottom-40 right-1/2 w-1 h-1 bg-gold-300/30 rounded-full animate-float"
        style={{ animationDelay: '3s' }}
      />

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-20">
          <div
            className="flex items-center justify-center gap-4 mb-8 transition-all duration-700 ease-out"
            style={{
              opacity: stage >= 1 ? 1 : 0,
              transform: stage >= 1 ? 'translateY(0)' : 'translateY(-15px)',
            }}
          >
            <div className="w-16 h-px bg-gradient-to-r from-transparent to-gold-500/50" />
            <span className="font-['Montserrat'] text-xs tracking-[0.3em] uppercase text-gold-400/70">
              Missão e Visão
            </span>
            <div className="w-16 h-px bg-gradient-to-l from-transparent to-gold-500/50" />
          </div>

          <h2
            className="font-['Playfair_Display'] text-4xl lg:text-6xl text-white font-light leading-tight mb-8 transition-all duration-800 ease-out"
            style={{
              opacity: stage >= 2 ? 1 : 0,
              transform: stage >= 2 ? 'translateY(0) scale(1)' : 'translateY(25px) scale(0.95)',
              filter: stage >= 2 ? 'blur(0px)' : 'blur(6px)',
            }}
          >
            Por que eu criei o{' '}
            <span className="italic text-gold-400">Carta do Eu Futuro</span>
          </h2>

          <p
            className="font-['Montserrat'] text-base lg:text-lg text-white/55 leading-relaxed font-light tracking-wide mb-6 transition-all duration-700 ease-out"
            style={{
              opacity: stage >= 3 ? 1 : 0,
              transform: stage >= 3 ? 'translateY(0)' : 'translateY(20px)',
            }}
          >
            Passei 30 anos da minha vida presa em pânico, ansiedade e traumas. Tentei de tudo — terapias, 
            remédios, técnicas — até que descobri o poder da escrita terapêutica combinada com PNL e 
            alta performance.
          </p>

          <p
            className="font-['Montserrat'] text-sm lg:text-base text-white/45 leading-relaxed font-light tracking-wide transition-all duration-700 ease-out"
            style={{
              opacity: stage >= 3 ? 1 : 0,
              transform: stage >= 3 ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: '150ms',
            }}
          >
            Quando escrevi minha primeira carta para o meu eu futuro, algo mudou. Pela primeira vez, 
            eu consegui ver além da dor. E decidi que ninguém mais precisaria sofrer 30 anos como eu sofri.
          </p>
        </div>

        {/* Frase de impacto destacada */}
        <div
          className="max-w-4xl mx-auto mb-20 transition-all duration-900 ease-out"
          style={{
            opacity: stage >= 4 ? 1 : 0,
            transform: stage >= 4 ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
          }}
        >
          <div className="relative bg-gradient-to-br from-gold-500/10 to-gold-600/5 border-l-4 border-gold-500 rounded-r-2xl p-10 lg:p-14 backdrop-blur-sm">
            {/* Quote icon */}
            <div className="absolute -top-4 -left-4 w-12 h-12 bg-gold-500 rounded-full flex items-center justify-center shadow-[0_8px_30px_rgba(201,168,76,0.3)]">
              <i className="ri-double-quotes-l text-2xl text-luxury-black w-6 h-6 flex items-center justify-center" />
            </div>

            <blockquote className="font-['Playfair_Display'] text-2xl lg:text-3xl text-white font-light italic leading-relaxed text-center">
              "Minha missão é dar a você as ferramentas que me salvaram — 
              para que você não precise esperar 30 anos para encontrar sua liberdade."
            </blockquote>

            <div className="flex items-center justify-center gap-3 mt-8">
              <div className="w-12 h-px bg-gold-500/40" />
              <span className="font-['Montserrat'] text-xs text-gold-400/70 tracking-wider">
                Bia Holmes
              </span>
              <div className="w-12 h-px bg-gold-500/40" />
            </div>
          </div>
        </div>

        {/* Cards de valores */}
        <div
          className="max-w-5xl mx-auto transition-all duration-700 ease-out"
          style={{
            opacity: stage >= 5 ? 1 : 0,
            transform: stage >= 5 ? 'translateY(0)' : 'translateY(25px)',
          }}
        >
          <h3 className="font-['Playfair_Display'] text-3xl text-white font-light text-center mb-12">
            Valores que guiam meu trabalho
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {valores.map((valor, index) => (
              <div
                key={index}
                className="group relative transition-all duration-500 ease-out"
                style={{
                  opacity: stage >= 5 ? 1 : 0,
                  transform: stage >= 5 ? 'translateY(0)' : 'translateY(20px)',
                  transitionDelay: `${index * 100}ms`,
                }}
              >
                <div className="relative h-full bg-luxury-charcoal/40 backdrop-blur-sm border border-gold-500/10 rounded-xl p-6 hover:border-gold-500/30 transition-all duration-500 hover:shadow-[0_8px_30px_rgba(201,168,76,0.12)] overflow-hidden">
                  {/* Glow on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-gold-500/0 to-gold-500/0 group-hover:from-gold-500/5 group-hover:to-gold-500/10 transition-all duration-500 rounded-xl" />

                  {/* Icon */}
                  <div className="relative mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold-500/20 to-gold-600/10 border border-gold-500/20 flex items-center justify-center group-hover:scale-110 transition-all duration-500">
                      <i className={`${valor.icon} text-2xl text-gold-400 w-6 h-6 flex items-center justify-center`} />
                    </div>
                  </div>

                  {/* Content */}
                  <h4 className="font-['Playfair_Display'] text-lg text-white font-semibold mb-2">
                    {valor.titulo}
                  </h4>

                  <p className="font-['Montserrat'] text-xs text-white/45 leading-relaxed font-light tracking-wide">
                    {valor.descricao}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom decorative line */}
        <div className="flex items-center justify-center gap-4 mt-20">
          <div className="w-24 h-px bg-gradient-to-r from-transparent to-gold-500/30" />
          <i className="ri-compass-3-line text-gold-500/40 text-xl w-6 h-6 flex items-center justify-center" />
          <div className="w-24 h-px bg-gradient-to-l from-transparent to-gold-500/30" />
        </div>
      </div>
    </section>
  );
};

export default MissaoVisao;